import React from 'react';
import { NumberField } from '../ui/number-field';
import { Label } from '../ui/label';
import { AlertTriangle, PoundSterling, Info } from 'lucide-react';

// Kept in sync with backend utils/fees.py MIN_GLOBAL_DEPOSIT_PERCENT
// (aligned with a conservative 20% floor so the deposit always clears the
// platform commission + processing fees with headroom)
const MIN_PCT = 20;
const MAX_PCT = 100;

// Used to preview the £ amount a client will pay, pick a realistic average.
// If the provider already has a draft service with a price we reflect that
// in the caller, otherwise fall back to this anchor.
const FALLBACK_PREVIEW_PRICE = 80;

// Warn when deposit < cancellation-fee-kept so providers understand the gap.
const POLICY_CANCEL_KEEP = { flexible: 0, moderate: 50, strict: 100 };

export function DepositSettingsCard({
  value,                    // null or int 10-100
  onChange,                 // (next: int|null) => void
  previewPrice,             // optional override £
  cancellationPolicy = 'flexible',
  showHeading = true,
  testIdPrefix = 'deposit',
}) {
  const numericPct = typeof value === 'number' && !Number.isNaN(value) ? value : null;
  const price = Number(previewPrice) > 0 ? Number(previewPrice) : FALLBACK_PREVIEW_PRICE;
  const clientPays = numericPct == null ? null : Math.round((price * numericPct) / 100 * 100) / 100;
  const remaining = clientPays == null ? null : Math.round((price - clientPays) * 100) / 100;

  const cancelKeep = POLICY_CANCEL_KEEP[cancellationPolicy] ?? 0;
  const policyMismatch = numericPct != null && numericPct < cancelKeep;

  return (
    <div className="space-y-5" data-testid={`${testIdPrefix}-settings-card`}>
      {showHeading && (
        <div>
          <h3 className="font-heading text-xl font-medium text-[#1A1A1A] flex items-center gap-2">
            <PoundSterling className="h-5 w-5 text-[#D4AF37]" />
            Default deposit
          </h3>
          <p className="text-sm text-stone-500 mt-1">
            The percentage of each booking clients pay upfront. This applies to every new service
            you add, and you can still override it per service later.
          </p>
        </div>
      )}

      <div className="card-surface p-4 sm:p-5 space-y-4">
        <div>
          <Label htmlFor={`${testIdPrefix}-percent-input`} className="text-sm text-[#1A1A1A]">
            Deposit percentage
          </Label>
          <div className="mt-1.5 flex flex-wrap items-center gap-3">
            <div className="relative w-[140px]">
              <NumberField
                value={numericPct}
                onChange={onChange}
                min={MIN_PCT}
                max={MAX_PCT}
                step="1"
                className="input-zuriya pr-8 font-medium"
                placeholder={`${MIN_PCT}`}
                testId={`${testIdPrefix}-percent-input`}
              />
              <span className="absolute right-3 top-[22px] -translate-y-1/2 text-stone-400 text-sm pointer-events-none">%</span>
            </div>
            {numericPct != null && (
              <button
                type="button"
                onClick={() => onChange(null)}
                className="text-xs text-stone-400 hover:text-stone-600 underline-offset-2 hover:underline"
                data-testid={`${testIdPrefix}-clear-btn`}
              >
                Clear
              </button>
            )}
          </div>
          <div
            className="mt-2 flex flex-wrap gap-1.5"
            data-testid={`${testIdPrefix}-percent-input-presets`}
          >
            {[20, 30, 50, 75, 100].map((preset) => {
              const active = numericPct === preset;
              return (
                <button
                  key={preset}
                  type="button"
                  onClick={() => onChange(preset)}
                  className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                    active
                      ? 'bg-[#D4AF37] border-[#D4AF37] text-white'
                      : 'bg-white border-[#E6E4DD] text-stone-600 hover:border-[#D4AF37] hover:text-[#1A1A1A]'
                  }`}
                  data-testid={`${testIdPrefix}-percent-input-preset-${preset}`}
                >
                  {preset}%
                </button>
              );
            })}
          </div>
          <p className="text-xs text-stone-400 mt-2 flex items-center gap-1.5">
            <Info className="h-3.5 w-3.5 text-[#D4AF37]" />
            Minimum allowed: {MIN_PCT}% · Maximum: {MAX_PCT}% · Any whole number between.
          </p>
        </div>

        {numericPct != null && (
          <div
            className="rounded-lg bg-[#F9F6F0] border border-[#EAE4D9] p-4 text-sm text-[#1A1A1A]"
            data-testid={`${testIdPrefix}-preview`}
          >
            <p className="text-stone-500 text-xs uppercase tracking-wide mb-1.5">What clients see</p>
            <p>
              On a{' '}
              <span className="font-medium">£{price.toFixed(2)}</span> service, clients will pay{' '}
              <span className="font-medium text-[#D4AF37]">£{clientPays?.toFixed(2)}</span> as a deposit
              upfront.
            </p>
            {remaining > 0 && (
              <p className="text-xs text-stone-500 mt-1">
                Remaining £{remaining.toFixed(2)} is paid on the day of the appointment.
              </p>
            )}
          </div>
        )}

        {policyMismatch && (
          <div
            className="rounded-lg border border-amber-200 bg-amber-50 p-3.5 flex gap-3 text-sm"
            data-testid={`${testIdPrefix}-policy-mismatch`}
          >
            <AlertTriangle className="h-4 w-4 text-amber-700 shrink-0 mt-0.5" />
            <p className="text-amber-900 leading-relaxed">
              Your deposit ({numericPct}%) is lower than your late-cancellation fee ({cancelKeep}%).
              If a client cancels late, you may not recover the full fee from the deposit alone.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
